import {useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {useParams} from 'react-router'
import {SearchBarHandle} from '../../search/searchBar/SearchBar'
import RouteContainer from '../../../common/styles/RouteContainer'
import {Card} from 'react-bootstrap'
import useBookIsbn from './useBookIsbn'
import NoContentForFullScreen from '../../../common/NoContentForFullScreen'
import CardBodyContentContainer from '../../../common/styles/CardBodyContentContainer'
import RowSpacer from '../../../common/styles/RowSpacer'
import BookIsbnSection from './BookIsbnSection'
import useUrlQuery from '../../../common/hooks/useUrlQuery'
import useLoginStore from '../../login/useLoginStore'
import searchCache from '../../search/searchBar/searchCache'
import IndexSearchBar from '../../index/card/IndexSearchBar'
import BookSourceOfflineLibrarySection from '../../search/bookSource/section/BookSourceOfflineLibrarySection'
import BookSourceOnlineLibrarySection from '../../search/bookSource/section/BookSourceOnlineLibrarySection'
import BookSourceSubscriptionSection from '../../search/bookSource/section/BookSourceSubscriptionSection'
import BookSourceOnlineUsedSection from '../../search/bookSource/section/BookSourceOnlineUsedSection'
import BookSourceOfflineUsedSection from '../../search/bookSource/section/BookSourceOfflineUsedSection'
import BookIsbnCardLoading from './BookIsbnCardLoading'
import LoadingBar from '../../../common/LoadingBar'
import parse from 'html-react-parser'
import useHtmlTitle from '../../../common/useHtmlTitle'
import BooksitoutIcon from '../../../config/BooksitoutIcon'
import ColorConfig from '../../../config/ColorConfig'
import useBookIsbnRelated from "./useBookIsbnRelated";
import useBookIsbnOtherEditions from "./useBookIsbnOtherEditions";
import BooksitoutImages from "../../../images/BooksitoutImages";

const BookIsbnRoute = () => {
    const {isbn} = useParams()
    const query = useUrlQuery('q')
    const [relatedBooks] = useBookIsbnRelated(isbn ?? null)
    const [otherEditions] = useBookIsbnOtherEditions(isbn ?? null)
    const [book, isLoading, isError] = useBookIsbn(isbn ?? null)

    useHtmlTitle(query ?? book?.title)

    const searchBarRef = useRef<SearchBarHandle>(null)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
                e.preventDefault()
                searchBarRef.current?.focus()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    const isLoggedIn = useLoginStore(state => state.isLoggedIn())
    useEffect(() => {
        if (!isLoggedIn) {
            searchCache.updateCache('search-book-histories', query?.toString() ?? '', `/book/${isbn}?q=${query}`, isbn ?? null)
        }
    }, [isLoggedIn, isbn, query])

    if (isLoading) {
        return (
            <RouteContainer>
                <RowSpacer/>
                <IndexSearchBar ref={searchBarRef}/>

                <RowSpacer size={40}/>
                <BookIsbnCardLoading/>

                <RowSpacer size={40}/>
                <Card>
                    <CardBodyContentContainer height={400}>
                        <Container>
                            <Title>소개</Title>
                            <LoadingBar size={15}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={20}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={5}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={12}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={7}/>
                            <RowSpacer size={5}/>
                        </Container>
                    </CardBodyContentContainer>
                </Card>

                <RowSpacer size={40}/>
                <Card>
                    <CardBodyContentContainer height={400}>
                        <Container>
                            <Title>목차</Title>
                            <LoadingBar size={15}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer/>

                            <LoadingBar size={15}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer size={5}/>

                            <LoadingBar size={8}/>
                            <RowSpacer size={5}/>
                        </Container>
                    </CardBodyContentContainer>
                </Card>

                <RowSpacer size={40}/>
            </RouteContainer>
        )
    }

    if (book == null || isError || isbn === undefined) {
        return <NoContentForFullScreen message={`찾으시는 책이 없어요<br/>(isbn = ${isbn})`}/>
    }

    return (
        <RouteContainer>
            <RowSpacer/>
            <IndexSearchBar ref={searchBarRef}/>

            <RowSpacer size={40}/>
            <BookIsbnSection book={book}/>

            {book.description != null && book.description !== '' && (
                <>
                    <RowSpacer size={40}/>
                    <CollapsableCard
                        content={
                            <Container>
                                <Title>소개</Title>

                                {book.shortDescription && (
                                    <>
                                        <ShortDescription>{parse(book.shortDescription)}</ShortDescription>
                                        <br/>
                                    </>
                                )}

                                <LongDescription>{parse(book.description.replaceAll('\n', '<br/>') ?? '-')}</LongDescription>
                            </Container>
                        }
                    />
                </>
            )}

            {book.tableOfContentsHtml && (
                <>
                    <RowSpacer size={40}/>
                    <CollapsableCard
                        content={
                            <Container>
                                <Title>목차</Title>

                                <TableOfContentsContainer>{parse(book.tableOfContentsHtml?.replaceAll('\n', '<br/>') ?? '-')}</TableOfContentsContainer>
                            </Container>
                        }
                    />
                </>
            )}

            {relatedBooks.length != 0 && (
                <>
                    <RowSpacer size={40}/>
                    <RelatedBooksOuter backgroundColorType='card'>
                        <Container>
                            <Title>관련 도서</Title>

                            <RowSpacer/>

                            <RelatedBooksCarouselWithButtons relatedBooks={relatedBooks}/>
                        </Container>
                    </RelatedBooksOuter>
                </>
            )}

            {otherEditions.length > 0 && (
                <>
                    <RowSpacer size={40}/>
                    <RelatedBooksOuter backgroundColorType='card'>
                        <Container>
                            <Title>같은 책</Title>

                            <RowSpacer/>

                            <CarouselContainer>
                                <RelatedBooksCarousel>
                                    {otherEditions.map(edition => (
                                        <Card key={edition.isbn13} className={'hover-effect'}>
                                            <CardBodyContentContainer height={230} backgroundColorType='card-card'>
                                                <RelatedBookItem href={`/book/${edition.isbn13}?q=${edition.title}`}>
                                                    <RelatedBookCoverWrapper>
                                                        <RelatedBookCover
                                                            src={edition.coverUrl ?? BooksitoutImages.Placeholder.bookCover}
                                                            alt={edition.title}
                                                            onError={(e: any) => e.target.src = BooksitoutImages.Placeholder.bookCover}
                                                        />
                                                    </RelatedBookCoverWrapper>
                                                    <RowSpacer size={10}/>
                                                    <RelatedBookTitle>{edition.title}</RelatedBookTitle>
                                                    {edition.authors && <RelatedBookAuthor>{edition.authors}</RelatedBookAuthor>}
                                                    <OtherEditionMemo>{edition.memo ?? '-'}</OtherEditionMemo>
                                                </RelatedBookItem>
                                            </CardBodyContentContainer>
                                        </Card>
                                    ))}
                                </RelatedBooksCarousel>
                            </CarouselContainer>
                        </Container>
                    </RelatedBooksOuter>
                </>
            )}

            <RowSpacer size={40}/>
            <BookSourceOfflineLibrarySection isbn={isbn}/>

            <RowSpacer size={40}/>
            <BookSourceOnlineLibrarySection isbn={isbn}/>

            <RowSpacer size={40}/>
            <BookSourceSubscriptionSection isbn={isbn}/>

            <RowSpacer size={40}/>
            <BookSourceOnlineUsedSection isbn={isbn}/>

            <RowSpacer size={40}/>
            <BookSourceOfflineUsedSection isbn={isbn}/>

            <RowSpacer size={40}/>
        </RouteContainer>
    )
}

const Container = styled.div.attrs({
    className: 'row',
})`
    height: 100%;
    padding: 20px;
`

const Title = styled.h1`
    font-size: 1.5rem;
    font-weight: bold;
    padding: 0;
    margin-bottom: 15px;
`

const TableOfContentsContainer = styled.p`
    h1 {
        font-size: 1.3rem;
        font-weight: bold;
        margin-top: 30px;
        padding: 0;
    }

    h2 {
        font-size: 1.1rem;
        margin-top: 30px;
        padding: 0;
    }

    h1:first-of-type {
        margin-top: 10px;
    }

    ul {
        list-style-position: outside;
    }

    padding-bottom: 0;
    margin-bottom: 0;
`

const RelatedBooksCarouselWithButtons = ({relatedBooks}) => {
    const carouselRef = useRef<HTMLDivElement | null>(null)

    return (
        <CarouselContainer>
            <RelatedBooksCarousel ref={carouselRef}>
                {relatedBooks.map((relatedBook) => {
                    let href = `/book/${relatedBook.isbn13}?q=${relatedBook.title}`;
                    return (
                        <Card key={relatedBook.isbn13} className={'hover-effect'}>
                            <CardBodyContentContainer height={200} backgroundColorType='card-card'>
                                <RelatedBookItem href={href}>
                                    <RelatedBookCoverWrapper>
                                        <RelatedBookCover src={relatedBook.coverUrl ?? BooksitoutImages.Placeholder.bookCover} alt={relatedBook.title} onError={(e) => e.target.src = BooksitoutImages.Placeholder.bookCover}/>
                                    </RelatedBookCoverWrapper>
                                    <RowSpacer size={10}/>
                                    <RelatedBookTitle>{relatedBook.title}</RelatedBookTitle>
                                    <RelatedBookAuthor>{relatedBook.authors}</RelatedBookAuthor>
                                </RelatedBookItem>
                            </CardBodyContentContainer>
                        </Card>
                    );
                })}
            </RelatedBooksCarousel>
        </CarouselContainer>
    )
}

const CollapsableCard = ({content}) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        const element = containerRef.current

        if (element) {
            // Compare scrollHeight and clientHeight to detect overflow
            setIsOverflowing(element.scrollHeight > element.clientHeight)
        }
    }, [content])

    return (
        <Card className={`${!isExpanded && isOverflowing && 'clickable'}`}
              onClick={() => isOverflowing && setIsExpanded(true)}>
            <CollapsableCardContainer ref={containerRef} height={isExpanded ? null : 400}
                                      isOverflowing={isOverflowing && !isExpanded}>
                {content}
                {isOverflowing &&
                    <CollapsableCardButton>{!isExpanded && <BooksitoutIcon.ArrowDown/>}</CollapsableCardButton>}
            </CollapsableCardContainer>
        </Card>
    )
}

const CollapsableCardContainer = styled(CardBodyContentContainer)`
    position: relative;
    min-height: ${({height}) => (height ? `${height}px` : 'auto')};
    max-height: ${({height}) => (height ? `${height}px` : 'none')};
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 50px;
        background: linear-gradient(to bottom, var(--gradient-start-color), var(--gradient-end-color));
        pointer-events: none;
        display: ${({isOverflowing}) => (isOverflowing ? 'block' : 'none')};
        opacity: ${({isOverflowing}) => (isOverflowing ? 1 : 0)};
    }
`

const CollapsableCardButton = styled.div.attrs({
    className: 'text-book',
})`
    font-size: 1.5rem;
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    cursor: pointer;
    z-index: 1;
`

const LongDescription = styled.div`
    font-size: 1em;
    color: var(--text-color);
    line-height: 1.7;

    p {
        margin-bottom: 16px;
    }

    br {
        margin-bottom: 8px;
    }
`

const ShortDescription = styled.div`
    font-style: italic;
    font-size: 1.1em;
    margin: 16px 0 40px;
    padding: 12px 16px;

    border-left: 4px solid ${ColorConfig.Primary};
    border-radius: 4px;
    line-height: 1.5;

    background-color: var(--card-card-bg-color);
    color: var(--text-color);
`

const RelatedBooksOuter = styled(CardBodyContentContainer)`
    overflow: visible;
`

const CarouselContainer = styled.div`
    position: relative;
    width: 100%;
    padding: 0;
`

const RelatedBooksCarousel = styled.div`
    display: flex;
    gap: 20px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding: 10px 5px;
    margin: -10px -5px;

    &::-webkit-scrollbar {
        display: none;
    }

    & > * {
        flex: 0 0 auto;
        scroll-snap-align: start;
        width: 150px;
    }
`

const RelatedBookItem = styled.a.attrs({
    className: ''
})`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    height: 100;
    z-index: 2;
`

const RelatedBookCoverWrapper = styled.div`
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: var(--card-card-bg-color);
`

const RelatedBookCover = styled.img`
    height: 100%;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    display: block;
    border-radius: 4px;
`

const RelatedBookTitle = styled.div.attrs(({
    className: 'clamp-1-line'
}))`
    font-size: 0.9rem;
    text-align: center;
    font-weight: 500;
    margin-bottom: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`

const RelatedBookAuthor = styled.div.attrs({
    className: 'clamp-1-line',
})`
    font-size: 0.8rem;
    text-align: center;
    color: var(--text-secondary-color);
`

const OtherEditionMemo = styled.div`
    font-size: 0.75rem;
    text-align: right;
    color: var(--text-secondary-color);
    font-style: italic;
    margin-top: 5px;
`

export default BookIsbnRoute
